/* AthleteSwap-Kopfzeile — Scroll-/Menü-/Such-Verhalten.
   1:1-Äquivalent zu den useEffect-Hooks in components/Kopfzeile.tsx, hier
   als schlichtes Vanilla-JS statt React-State, da Shopify-Sections
   serverseitig gerendert werden.

   Header-State (transparent über dem Hero vs. weiß):
   Früher lief das über eine feste window.scrollY > 24px-Schwelle. Das war
   auf Desktop (≥990px) faktisch immer falsch: Horizons "Squeeze"-Layout
   setzt dort html/body auf overflow:hidden und scrollt stattdessen
   .page-wrapper intern (siehe assets/scroll-container.js) — window.scrollY
   bleibt in diesem Layout dauerhaft 0, wodurch der Header nie in den
   "weiß"-Zustand wechselte, egal wie weit man scrollte. Genau das war die
   Ursache für den in der Theme-Editor-Vorschau (fast immer ein breites
   Desktop-Fenster) hängenbleibenden transparenten Header. Mobile (<990px)
   scrollt nativ auf document, deshalb funktionierte es dort schon vorher.
   Der Fix nutzt daher denselben Scroll-Container wie der Rest des Themes
   (@theme/scroll-container, auch von assets/header.js verwendet) statt
   window/document fest anzunehmen, und ermittelt den Zustand per
   IntersectionObserver auf einem Sentinel im Hero statt einer festen
   Pixel-Schwelle — dadurch unabhängig von der Hero-Höhe korrekt. */
import { getIntersectionRoot, getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

(function () {
  var heroBeobachter = null;
  var scrollContainer = null;
  var scrollHandler = null;

  function scrollLogikAufraeumen() {
    if (heroBeobachter) {
      heroBeobachter.disconnect();
      heroBeobachter = null;
    }
    if (scrollContainer && scrollHandler) {
      scrollContainer.removeEventListener('scroll', scrollHandler);
    }
    scrollContainer = null;
    scrollHandler = null;
  }

  /* Einziger Header-State: "over-hero" (transparent, solange der Hero noch
     sichtbar ist) oder "scrolled" (weiß). Läuft bei jedem
     shopify:section:load und bei jedem Wechsel des Squeeze-Breakpoints neu,
     damit der Zustand auch nach einem Section-Rerender im Theme-Editor und
     nach einer Fensterbreiten-Änderung zuverlässig weiter aktualisiert wird
     (sonst bliebe eine frisch ersetzte Kopfzeile für immer im
     serverseitigen Ausgangszustand hängen, bzw. würde der alte
     Scroll-Container weiter beobachtet). */
  function headerStateInitialisieren() {
    var header = document.querySelector('[data-as-header]');
    scrollLogikAufraeumen();
    if (!header) return;

    if (header.getAttribute('data-as-ueberlagernd') !== 'true') {
      header.setAttribute('data-as-header-state', 'scrolled');
      return;
    }

    var sentinel = document.querySelector('[data-as-header-hero-sentinel]');
    if (sentinel && 'IntersectionObserver' in window) {
      heroBeobachter = new IntersectionObserver(
        function (eintraege) {
          var eintrag = eintraege[eintraege.length - 1];
          header.setAttribute('data-as-header-state', eintrag.isIntersecting ? 'over-hero' : 'scrolled');
        },
        { root: getIntersectionRoot(), threshold: 0 }
      );
      heroBeobachter.observe(sentinel);
      return;
    }

    // Fallback ohne Sentinel/IntersectionObserver: Scroll-Schwelle, aber auf
    // dem tatsächlichen Scroll-Container (nicht fest window/document).
    scrollContainer = getScrollEventTarget();
    scrollHandler = function () {
      header.setAttribute('data-as-header-state', getScrollTop() > 24 ? 'scrolled' : 'over-hero');
    };
    scrollHandler();
    scrollContainer.addEventListener('scroll', scrollHandler, { passive: true });
  }

  // Menü/Suche: per Event-Delegation auf document statt direkt an die
  // Buttons gebunden. So funktioniert es unabhängig davon, ob Shopify die
  // Kopfzeile im Theme-Editor zwischendurch neu rendert — es wird nie neu
  // gebunden, es kann also auch nie zu doppelten Listenern kommen.
  function findeUeber(ziel, selektor) {
    return ziel && ziel.closest ? ziel.closest(selektor) : null;
  }

  // Suche läuft seit dem Wechsel auf das native Horizon-Predictive-Search-
  // Modal (search-modal.liquid) über dessen eigene deklarative
  // on:click="#search-modal/showDialog"-Anbindung — hier nur noch das Menü.
  document.addEventListener('click', function (e) {
    var menuOpenBtn = findeUeber(e.target, '[data-as-menu-open]');
    var menuCloseBtn = findeUeber(e.target, '[data-as-menu-close]');
    if (!menuOpenBtn && !menuCloseBtn) return;

    if (menuOpenBtn) {
      var menu = document.querySelector('[data-as-menu]');
      if (!menu) return;
      menu.hidden = false;
      menuOpenBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    } else if (menuCloseBtn) {
      var menuZuSchliessen = document.querySelector('[data-as-menu]');
      if (!menuZuSchliessen) return;
      menuZuSchliessen.hidden = true;
      document.body.style.overflow = '';
      var opener = document.querySelector('[data-as-menu-open]');
      if (opener) opener.setAttribute('aria-expanded', 'false');
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;

    var menu = document.querySelector('[data-as-menu]');
    if (menu && !menu.hidden) {
      menu.hidden = true;
      document.body.style.overflow = '';
      var opener = document.querySelector('[data-as-menu-open]');
      if (opener) opener.setAttribute('aria-expanded', 'false');
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', headerStateInitialisieren);
  } else {
    headerStateInitialisieren();
  }
  document.addEventListener('shopify:section:load', headerStateInitialisieren);
  scrollContainerMediaQuery.addEventListener('change', headerStateInitialisieren);
})();
